import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { unwrapList } from '../lib/unwrap'

export interface Rule {
  id: string
  name: string
  description: string
  event_type: string
  enabled: boolean
  status: string // 'active' | 'inactive'
  conditions: object
  actions: object[]
  created_at: string
}

export interface Alert {
  id: string
  rule_id: string
  device_id: string
  severity: string
  message: string
  acknowledged: boolean
  created_at: string
}

export function useRules() {
  return useQuery({
    queryKey: ['rules'],
    queryFn: () => api.get('/rules').then((r) => unwrapList<Rule>(r.data)),
  })
}

export function useCreateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description: string; event_type: string; conditions: object; actions: object[] }) =>
      api.post<Rule>('/rules', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  })
}

export function useUpdateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Rule> & { id: string }) =>
      api.put<Rule>(`/rules/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  })
}

export function useToggleRuleStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/rules/${id}/status`, { active }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  })
}

export function useDeleteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  })
}

export function useAlerts(params?: { device_id?: string; severity?: string; limit?: number }) {
  return useQuery({
    queryKey: ['alerts', params],
    queryFn: () => api.get('/alerts', { params }).then((r) => unwrapList<Alert>(r.data)),
  })
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/alerts/${id}/acknowledge`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  })
}
